import React, { useState } from 'react';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Dialog } from '@mui/material';

import { DEFAULT_PAGE_LOAD_SIZE } from '../../../api/pagination';
import {
    clearBusinessLogicError,
    updateCourseModuleParticipants
} from '../../../store/courseService';
import { selectAttendedStatusId, selectBarrierDetails } from '../../../store/dataSelectors';
import { loadBarrierNameDetails } from '../../../store/directusService';
import { clearParticipantsSearch, searchParticipants } from '../../../store/participantService';
import { createActionPlan } from '../../../store/participantService';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { checkDiff } from '../../../utils/formValidation/errorCheckingFunctions';
import { stableSort } from '../../../utils/sortFunctions';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ScreenError from '../../ui/messageBanners/ScreenError';

import FullCourseAttendanceRow from './FullCourseAttendanceRow';

import modal from '../../../commonStyles/modal.module.css';
import local from '../courseStyles/fullCourseAttendanceStyles.module.css';

const headCells = [
    { id: 'id', numeric: false, label: 'Participant ID' },
    { id: 'firstName', numeric: false, label: 'First Name' },
    { id: 'lastName', numeric: false, label: 'Last Name' },
    { id: 'status', numeric: false, label: 'Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'id',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};
const _TIMEOUT = import.meta.env.MODE === 'test' ? 2000 : 30 * 1000;

const FullCourseAttendanceDialog = ({ module, isOpen, onClose, isSelfEmployedCoach }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const _MESSAGE = `Module details have been updated for module ${module.title}`;
    const [rows, setRows] = useState([]);
    const [updatedParticipants, setUpdatedParticipants] = useState([]);
    const [actionPlanData, setActionPlanData] = useState([]);
    const [currentParticipantId, setCurrentParticipantId] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [showCells, setShowCells] = useState(false);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const participants = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const attendedId = useSelector(selectAttendedStatusId);
    const barrierNameDetails = useSelector(selectBarrierDetails);
    const currentTemplate = useSelector((state) => state.entities.courseService.currentTemplate);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const businessLogicError = useSelector(
        (state) => state.entities.courseService.businessLogicError
    );

    // USEEFFECTS
    useEffect(() => {
        if (barrierNameDetails?.length < 1) dispatch(loadBarrierNameDetails());
        const ids = module?.participants?.map((el) => el.participantId);
        ids.length > 0
            ? dispatch(searchParticipants({ ids }, 0, DEFAULT_PAGE_LOAD_SIZE))
            : dispatch(clearParticipantsSearch());
        setUpdatedParticipants(module.participants);
    }, [module]);

    useEffect(() => {
        if (!participants?.length) {
            setLoadingContentMessage('...loading');
            rows.length && setRows([]);
        } else {
            const rows = participants.map((el) => ({
                id: el.id,
                contractId: el.contractId,
                ptCode: el.ptCode,
                firstName: el.firstName,
                lastName: el.lastName,
                notes:
                    module?.participants?.find((entry) => entry.participantId === el.id)?.notes ||
                    ''
            }));
            setRows(rows);
        }
    }, [participants]);

    useEffect(() => {
        if (!currentParticipantId) return;
        if (actionPlanData.some((el) => el.participantId === currentParticipantId)) return;
        const actionPlan = {
            participantId: currentParticipantId,
            mandatory: '',
            actionTitle: module.smartTarget.name,
            action: module.smartTarget.action,
            actionStartDate: '',
            actionDueDate: '',
            actionOutcomeDate: '',
            outcomeId: '',
            moduleId: module.id,
            comments: ''
        };
        setActionPlanData((prev) => [...prev, actionPlan]);
    }, [currentParticipantId]);

    useEffect(() => {
        if (!showCells) return;
        const timer = setTimeout(() => {
            setShowCells(false);
        }, _TIMEOUT);

        return () => {
            clearTimeout(timer);
        };
    }, [_TIMEOUT, showCells]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) {
            if (currentParticipantId) {
                actionPlanData.length > 0 &&
                    actionPlanData.forEach((el) => dispatch(createActionPlan(el)));
                setCurrentParticipantId('');
                setActionPlanData([]);
            }
            if (currentTemplate?.healthInformationIds?.length) {
                const attendedParticipantIds = updatedParticipants
                    .filter((el) => el.statusId === attendedId)
                    .map((el) => el.participantId);
                if (attendedParticipantIds.length) {
                    dispatch(searchParticipants({ ids: attendedParticipantIds }, 0));
                    navigate('/module_attendance_details', {
                        state: {
                            participantIds: attendedParticipantIds,
                            module
                        }
                    });
                }
            }
            onModalExit();
        } else if (businessLogicError && Object.keys(businessLogicError).length > 0) {
            setErrors(businessLogicError);
            dispatch(clearBusinessLogicError());
        }
    }, [successMessage, actionPlanData, businessLogicError]);

    // FNs
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <FullCourseAttendanceRow
                key={el.id}
                row={el}
                module={module}
                showCells={showCells}
                onUpdateParticipantDetail={onUpdateParticipantDetail}
                isSelfEmployedCoach={isSelfEmployedCoach}
            />
        ));

    // EVENT HANDLERS
    const onUpdateParticipantDetail = (statusId, participantId) => {
        const modified = updatedParticipants.map((el) =>
            el.participantId === participantId ? { ...el, statusId } : el
        );
        setUpdatedParticipants(modified);

        if (
            module?.smartTarget &&
            attendedId &&
            statusId === attendedId &&
            barrierNameDetails.length > 0
        ) {
            setCurrentParticipantId(participantId);
        }
    };

    const onModalExit = () => onClose(false);

    const onUpdate = () => {
        setIsUpdateButtonDisabled(true);
        const newErrors = checkDiff(updatedParticipants, module.participants);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        const payload = { participants: updatedParticipants };
        dispatch(updateCourseModuleParticipants(module.id, payload, _MESSAGE));
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapper"
            open={isOpen}
            onClose={onModalExit}
            fullWidth
            aria-labelledby="form-dialog-title">
            <div
                className={`${modal.modalHeader} ${local.heading}`}
                id="full_course_attendance_modal_title">
                <h3>Course Booking Confirmation</h3>
            </div>
            <div className={modal.modalContent}>
                <div className={local.innerContent}>
                    <h3 className={local.subHeading}>
                        The following participants are currently booked on this course
                    </h3>
                    <div className={local.tableContent}>
                        {rows.length < 1 ? (
                            <LoadingSpinner content={loadingContentMessage} />
                        ) : (
                            <ResultsTable
                                defaultOrderBy={rowMetaData.orderBy}
                                sortOrder={rowMetaData.order}
                                headCells={headCells}
                                passRowMetaDataUp={setRowMetaData}
                                tableRows={createRows()}
                                totalResults={rows.length}
                                onRevealSensitiveData={() => setShowCells(true)}
                                showSensitiveCells={showCells}
                                customSensitivityNoticeClass="modalTable"
                            />
                        )}
                    </div>

                    {errors.businessLogicError?.error && (
                        <ScreenError
                            error={errors.businessLogicError}
                            clearError={clearError}
                            customClass="modalBaseVerticalAlign"
                        />
                    )}

                    <div className={modal.actionButtons}>
                        <Button
                            id="updateAttendanceModalButton"
                            type="button"
                            disabled={isUpdateButtonDisabled}
                            content="Update"
                            customErrorClass="formActionsError"
                            error={errors.button}
                            clearError={() => clearError('button')}
                            onClick={onUpdate}
                        />
                        <Button
                            id="closeAttendanceModalButton"
                            type="button"
                            content="Close"
                            onClick={onModalExit}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

FullCourseAttendanceDialog.propTypes = {
    module: PropTypes.object,
    isSelfEmployedCoach: PropTypes.bool,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
};

export default FullCourseAttendanceDialog;
